import { ColumnsType } from 'antd/lib/table';
import React, { FC } from 'react';
import styled from 'styled-components';
import {
  FinancialsProjectSettings,
  ProjectFinancialsContractType,
} from 'api/project';
import { Loading } from 'components/atoms/loading';
// import { ProjectFinancialsSettingsContext } from 'contexts/project-financials-settings';
import { useTranslate } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { useUserTeamsRateInfo } from './hooks/use-user-team-rate-info';
import {
  // CostRateRenderer,
  GetAvatar,
  HourRateRenderer,
  LastUpdatedRenderer,
  RoleCellRenderer,
} from './member-rate-renderers';
import { StyledTable } from './styles';
import { CustomBillRateLookup, RateInfoRowData } from './types';

const Loader = styled(Loading)`
  padding-left: var(--spacing-05);
`;

const TeamMemberBillRateTable = styled(StyledTable)`
  margin-top: 0;
  cursor: default;
`;

const Header = styled.div`
  height: var(--spacing-08);
  padding: var(--spacing-04);
  background-color: var(--coolest-gray-10);
`;

const getSortString = (entry: RateInfoRowData) => {
  if ('referenceTeam' in entry) {
    return fullName(entry.user);
  }

  if ('team' in entry) {
    return entry.team.teamName;
  }

  if ('user' in entry) {
    return fullName(entry.user);
  }

  return '';
};

export const TeamMembersRateInfo: FC<{
  showHeader?: boolean;
  projectId: number;
  data: FinancialsProjectSettings;
  rateCardVersionId?: number;
  readonly?: boolean;
  overrideBillRate?: boolean;
  overrideRateCardId?: number;
  customBillRateMap?: CustomBillRateLookup;
  updateBillRateLookUp?: (lookup) => void;
}> = ({
  projectId,
  data,
  showHeader,
  readonly,
  overrideRateCardId,
  overrideBillRate,
  customBillRateMap,
  updateBillRateLookUp,
  rateCardVersionId,
}) => {
  const translate = useTranslate();
  const rateCardId = overrideRateCardId || data?.rateCard?.rateCardId;
  // const { permissionsMap } = useContext(ProjectFinancialsSettingsContext);
  // const {
  //   projectProfitability: { canRead: canReadCostRates },
  // } = permissionsMap;

  const { userTeamInfo, isInfoLoading, isRoleBillLookupLoading } =
    useUserTeamsRateInfo({
      projectId,
      rateCardId,
      rateCardVersionId,
      customBillRateMap,
      overrideBillRate,
    });

  const columns: ColumnsType<RateInfoRowData> = [
    {
      title: translate('project.settings.budget.rate.table.team_members'),
      key: 'teamMembers',
      width: '25%',
      render: (record) => {
        return <GetAvatar data={record} />;
      },
      sorter: (a, b) => sortByText(getSortString(a), getSortString(b)),
    },
    {
      title: translate('project.settings.budget.rate.table.role'),
      key: 'role',
      width: '25%',
      render: (record) => {
        return <RoleCellRenderer data={record} />;
      },
    },
    ...(data.financialContractType !== ProjectFinancialsContractType.NONBILLABLE
      ? [
          {
            title: translate('project.settings.budget.rate.table.hour_rate'),
            key: 'hourRate',
            width: '15%',
            render: (record) => {
              return isRoleBillLookupLoading ? (
                <Loader />
              ) : (
                <HourRateRenderer
                  data={record}
                  readonly={readonly}
                  updateBillRateLookUp={updateBillRateLookUp}
                />
              );
            },
          },
        ]
      : []),
    // ...(canReadCostRates
    //   ? [
    //       {
    //         title: translate('project.settings.budget.rate.table.cost_rate'),
    //         key: 'costRate',
    //         width: '15%',
    //         render: (record) => {
    //           return isCostRatesLoading ? (
    //             <Loader />
    //           ) : (
    //             <CostRateRenderer data={record} />
    //           );
    //         },
    //       },
    //     ]
    //   : []),
    ...(overrideBillRate && readonly
      ? [
          {
            title: translate('project.settings.budget.rate.table.last_updated'),
            key: 'lastUpdated',
            width: '20%',
            render: (record) => {
              return <LastUpdatedRenderer data={record} />;
            },
          },
        ]
      : []),
  ];

  return (
    <>
      {showHeader ? (
        <Header>
          {translate('project.settings.team_members.bill.table.header')}
        </Header>
      ) : null}
      <TeamMemberBillRateTable
        loading={isInfoLoading}
        columns={columns}
        dataSource={userTeamInfo}
        pagination={false}
      />
    </>
  );
};
